import { invariant } from '../util/invariant';
import type { Mp4Info } from './mp4Types';
import { extractH264FrameInfoFromAvcCBox, getCodecString, getH264FrameInfo, getMp4ProgressiveStatus, type Avc1VisualSampleEntry, type AvcC, type Mp4Box } from './parser';
import { findMp4aBox, readMp4aSampleEntry, readTrakBoxes } from './trakBoxes';
import { populateVirtualSegments } from './virtualSegments';

export function inspectMoov(
  mp4Info: Mp4Info | undefined,
  moovBox: Mp4Box,
  virtualSegmentUrl: string | undefined,
  ignoreVideo: boolean,
  ignoreAudio: boolean,
): Mp4Info {
  const videoTracks = mp4Info ? mp4Info.videoTracks : [];
  const audioTracks = mp4Info ? mp4Info.audioTracks : [];
  let virtualSegments = mp4Info ? mp4Info.virtualSegments : null;

  const nExistingAudioTracks = audioTracks.length;

  let videoStblBoxes: Mp4Box[] = [];
  let audioStblBoxes: Mp4Box[] = [];

  moovBox.children.forEach(box => {
    if (box.type === 'trak') {
      const { trackId, mediaStartPtsOffset, timescale, handlerType, stblBox, stsdBox } = readTrakBoxes(box);

      if (handlerType === 'vide' && !ignoreVideo) {
        // Video
        const avc1Box = stsdBox.children.find(b => b.type === 'avc1') as Avc1VisualSampleEntry | undefined;
        invariant(avc1Box, 'Could not find avc1 box');

        const avcCBox = avc1Box.children.find(b => b.type === 'avcC') as AvcC | undefined;
        invariant(avcCBox, 'Could not find avcC box');
        const avcC = new Uint8Array(avcCBox.avcC);
        const width = avc1Box.width;
        const height = avc1Box.height;

        const h264FrameInfo = extractH264FrameInfoFromAvcCBox(avcCBox);

        videoTracks.push({
          codecString: getCodecString(avcCBox),
          progressiveStatus: getMp4ProgressiveStatus(h264FrameInfo),
          pixelAspectRatio: (h264FrameInfo !== null) ? h264FrameInfo.sampleAspectRatio : undefined,
          trackId,
          timescale,
          avcC,
          width,
          height,
          frameDuration: 0,
          mediaStartPtsOffset,
          nalLengthSize: avcCBox.lengthSizeMinusOne + 1,
          frameInfo: getH264FrameInfo(avcCBox),
        });
        videoStblBoxes.push(stblBox);
      } else if (handlerType === 'soun' && !ignoreAudio) {
        const mp4aBox = findMp4aBox(stsdBox);
        invariant(mp4aBox, 'Could not find mp4a box');
        const { sampleRate, bitsPerSample, channelCount, codecSpecificData } = readMp4aSampleEntry(mp4aBox, timescale);

        // Audio
        audioTracks.push({
          trackId,
          timescale,
          sampleRate,
          bitsPerSample,
          channelCount,
          frameDuration: 0,
          mediaStartPtsOffset,
          codecSpecificData,
        });
        audioStblBoxes.push(stblBox);
      }
    }
  });

  if (virtualSegmentUrl !== undefined) {
    // If we have video, we want to create the virtual segments after the GOPs
    invariant(videoTracks.length <= 1, 'Cannot handle more than 1 video stream');
    invariant(videoStblBoxes.length <= 1, 'Cannot handle more than 1 video stream inside file');
    if (videoStblBoxes.length === 1) {
      virtualSegments = populateVirtualSegments(virtualSegmentUrl, virtualSegments, null, videoTracks[0]!.mediaStartPtsOffset, videoTracks[0]!.timescale, videoStblBoxes[0]!);
      if (virtualSegments !== null && virtualSegments.length > 0 && virtualSegments[0]!.videoData!.samples.length > 0) {
        videoTracks[0]!.frameDuration = virtualSegments[0]!.videoData!.samples[0]!.duration;
      }
    }

    for (let i = nExistingAudioTracks; i < audioTracks.length; i++) {
      virtualSegments = populateVirtualSegments(virtualSegmentUrl, virtualSegments, i, audioTracks[i]!.mediaStartPtsOffset, audioTracks[i]!.timescale, audioStblBoxes[i - nExistingAudioTracks]!);
      if (virtualSegments !== null && virtualSegments.length > 0 && virtualSegments[0]!.audioData[i]!.samples.length > 0) {
        audioTracks[i]!.frameDuration = virtualSegments[0]!.audioData[i]!.samples[0]!.duration;
      }
    }
  }

  return {
    videoTracks,
    audioTracks,
    virtualSegments: virtualSegments ?? [],
  };
}
